import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { sdk } from '@/services/sdk';
import LookupPicker from '@/components/LookupPicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Busy, Field, Panel } from '@/components/kit';
import { PickSelect, SwitchRow } from '@/components/inputs';
import { RatingPicker } from '@/components/account/ReviewDialog';
import { idOf } from '@/shared/data';
import { PLAN_VALUES, planName } from '@/shared/pricing';

export const PLAN_LABELS = Object.fromEntries(PLAN_VALUES.map((p) => [p, planName(p)]));

export const VISIBILITY_STYLES = {
  Visible: 'border-success-border bg-success-muted text-success',
  Hidden: 'border-border bg-muted text-muted-foreground',
};

export function reviewToForm(r) {
  return {
    Name: r?.Name ?? '',
    order_c: idOf(r?.order_c),
    display_name_c: r?.display_name_c ?? '',
    plan_c: r?.plan_c ?? '',
    rating_c: Number(r?.rating_c || 5),
    body_c: r?.body_c ?? '',
    hidden_c: !!r?.hidden_c,
    reply_c: r?.reply_c ?? '',
  };
}

export const blankReview = () => reviewToForm(null);

function shortName(full) {
  const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0]}.`;
}

/** Full review form for the New and Edit pages. `onSubmit` receives the write payload. */
export default function ReviewForm({ initial, onSubmit, saving, submitLabel = 'Save review', busyLabel = 'Saving…', cancelTo }) {
  const [form, setForm] = useState(initial);
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const text = (key) => (e) => set(key)(e.target.value);

  async function pickOrder(id) {
    set('order_c')(id || null);
    if (!id) return;
    const r = await sdk.table('orders_c').select(['Name', 'plan_c', 'full_name_c']).get(id);
    const order = r?.data;
    if (!order) return;
    setForm((f) => ({
      ...f,
      plan_c: f.plan_c || order.plan_c || '',
      display_name_c: f.display_name_c || shortName(order.full_name_c),
    }));
  }

  function submit(e) {
    e.preventDefault();
    if (!form.Name.trim()) return toast.error('Add a headline');
    if (!form.order_c) return toast.error('Pick the order this review is about');
    if (!form.body_c.trim()) return toast.error('Add the review text');
    return onSubmit({
      Name: form.Name.trim(),
      order_c: form.order_c,
      display_name_c: form.display_name_c.trim() || 'Anonymous viewer',
      plan_c: form.plan_c || 'Demon',
      rating_c: Number(form.rating_c || 5),
      body_c: form.body_c.trim(),
      hidden_c: !!form.hidden_c,
      reply_c: form.reply_c.trim(),
    });
  }

  return (
    <form onSubmit={submit}>
      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="The review" icon="Star" bodyClassName="space-y-4 py-4">
          <Field label="Rating">
            <RatingPicker value={form.rating_c} onChange={set('rating_c')} />
          </Field>
          <Field label="Headline" htmlFor="rv-name">
            <Input id="rv-name" value={form.Name} onChange={text('Name')} placeholder="Set up in five minutes, zero buffering" />
          </Field>
          <Field label="Review" htmlFor="rv-body">
            <Textarea
              id="rv-body"
              rows={6}
              value={form.body_c}
              onChange={text('body_c')}
              placeholder="What they watch, on which devices, and how it has held up…"
            />
          </Field>
        </Panel>

        <Panel title="Who and what" icon="User" bodyClassName="space-y-4 py-4">
          <Field label="Order" htmlFor="rv-order" hint="Fills in the plan and the display name.">
            <LookupPicker
              table="orders_c"
              searchField="Name"
              value={form.order_c}
              onChange={pickOrder}
              placeholder="Search order codes…"
              clearable
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Shown as" htmlFor="rv-display" hint="First name and initial.">
              <Input id="rv-display" value={form.display_name_c} onChange={text('display_name_c')} placeholder="Maya O." />
            </Field>
            <Field label="Plan" htmlFor="rv-plan">
              <PickSelect
                id="rv-plan"
                value={form.plan_c}
                onChange={set('plan_c')}
                values={PLAN_VALUES}
                labels={PLAN_LABELS}
                placeholder="Which plan"
              />
            </Field>
          </div>
          <SwitchRow
            id="rv-hidden"
            label="Hidden from the storefront"
            hint="Hidden reviews stay on file but never show publicly."
            checked={form.hidden_c}
            onChange={set('hidden_c')}
          />
        </Panel>

        <Panel title="Public reply" icon="Reply" className="lg:col-span-2" bodyClassName="py-4">
          <Field label="Reply from Ghost Cable" htmlFor="rv-reply" hint="Shown under the review. Leave empty for no reply.">
            <Textarea
              id="rv-reply"
              rows={3}
              value={form.reply_c}
              onChange={text('reply_c')}
              placeholder="Thanks for sticking with us through the winter…"
            />
          </Field>
        </Panel>
      </div>

      <div className="sticky bottom-0 z-10 mt-6 flex justify-end gap-2 border-t border-border bg-background/95 py-3 backdrop-blur">
        <Button type="button" variant="outline" asChild>
          <Link to={cancelTo}>Cancel</Link>
        </Button>
        <Button type="submit" disabled={saving}>
          <Busy busy={saving} label={submitLabel} busyLabel={busyLabel} />
        </Button>
      </div>
    </form>
  );
}
